import * as React from 'react'
import * as PropTypes from 'prop-types'
import { css, Rule } from 'glamor'

import { isKey } from '../../utils'
import { useTheme } from '../../theme'

import getStyles from './styles'

const sizes = ['regular', 'small']
const variants = ['regular', 'dim', 'white', 'inherit']

interface IParagraphProps {
  children?: React.ReactNode
  variant?: 'regular' | 'dim' | 'white' | 'inherit'
  size?: 'regular' | 'small'
  textAlign?: 'left' | 'right' | 'center' | 'justify' | 'initial' | 'inherit'
  bold?: boolean
  tight?: boolean
  flush?: boolean
  flushLastChild?: boolean
  wide?: boolean
  style?: Rule
  tag?: string | React.ComponentType<{}>
}

/**
 * @type { React.StatelessComponent }
 */
export function Paragraph({
  children,
  size,
  variant,
  textAlign,
  bold,
  tight,
  flush,
  flushLastChild,
  style,
  tag: Component,
  wide,
}: IParagraphProps) {
  const styles = getStyles(useTheme())

  if (!Component) {
    return null
  }

  return (
    <Component
      {...css([
        styles.base,
        isKey(styles.sizes, size) && styles.sizes[size],
        isKey(styles.variants, variant) && styles.variants[variant],
        bold && styles.bold,
        tight && styles.tight,
        flush && styles.flush,
        flushLastChild && styles.flushLastChild,
        wide && styles.wide,
        textAlign && { textAlign },
        style,
      ])}
    >
      {children}
    </Component>
  )
}

Paragraph.displayName = 'Paragraph'

Paragraph.propTypes = {
  tag: PropTypes.string,
  children: PropTypes.node,
  variant: PropTypes.oneOf(variants),
  size: PropTypes.oneOf(sizes),
  textAlign: PropTypes.oneOf(['left', 'right', 'center', 'justify', 'initial', 'inherit']),
  bold: PropTypes.bool,
  tight: PropTypes.bool,
  flush: PropTypes.bool,
  flushLastChild: PropTypes.bool,
  wide: PropTypes.bool,
  style: PropTypes.object,
}

Paragraph.defaultProps = {
  tag: 'p',
  variant: 'inherit',
  textAlign: 'inherit',
  size: 'regular',
  wide: false,
}

Paragraph.VARIANTS = variants
Paragraph.SIZES = sizes
